import { collection, deleteDoc, doc, getDocs, limit, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { DEFAULT_PUBLIC_WEB_MEDIA_POLICY, formatBytesMb, loadPublicWebMediaPolicy, type PublicWebMediaPolicy } from "../utils/publicWebMediaPolicy";

type PublicPerson = {
  id: string;
  name: string;
  role: string;
  bio: string;
  email: string;
  imageUrl: string;
  imageStoragePath?: string;
  sortOrder: number;
  isPublished: boolean;
};

type PublicPeoplePageProps = {
  collectionName: "publicBoard" | "publicPtuLeadership";
  title: string;
  description: string;
};

type CropState = {
  src: string;
  fileName: string;
  naturalWidth: number;
  naturalHeight: number;
  offsetX: number;
  offsetY: number;
  zoom: number;
};

const EMPTY: PublicPerson = {
  id: "",
  name: "",
  role: "",
  bio: "",
  email: "",
  imageUrl: "",
  imageStoragePath: "",
  sortOrder: 100,
  isPublished: true,
};

const EDITOR_SIZE = 320;
const OUTPUT_SIZE = 512;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const MAX_OFFSET = 180;

function asNumber(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getInitials(name: string, role: string) {
  const source = name.trim() || role.trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function InitialAvatar({ name, role, size = 42 }: { name: string; role: string; size?: number }) {
  return (
    <div
      className="avatar"
      aria-label={`${name || role || "Person"} photo placeholder`}
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(135deg, #f6edd8, #d8a84d)",
        border: "1px solid var(--border)",
        color: "#2b2114",
        fontWeight: 800,
        letterSpacing: 0,
      }}
    >
      {getInitials(name, role)}
    </div>
  );
}

function getEditorImageSize(crop: CropState) {
  const scale = Math.max(EDITOR_SIZE / crop.naturalWidth, EDITOR_SIZE / crop.naturalHeight) * crop.zoom;
  return {
    width: crop.naturalWidth * scale,
    height: crop.naturalHeight * scale,
  };
}

function clampOffset(value: number) {
  return Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, value));
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read image file."));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load image."));
    image.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Could not create cropped image."));
    }, "image/png");
  });
}

async function imageUrlToDataUrl(url: string) {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Could not load existing image for editing.");
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read existing image."));
    reader.readAsDataURL(blob);
  });
}

function mapDoc(id: string, data: any): PublicPerson {
  return {
    id,
    name: String(data?.name ?? ""),
    role: String(data?.role ?? ""),
    bio: String(data?.bio ?? ""),
    email: String(data?.email ?? ""),
    imageUrl: String(data?.imageUrl ?? ""),
    imageStoragePath: String(data?.imageStoragePath ?? ""),
    sortOrder: asNumber(data?.sortOrder, 999),
    isPublished: data?.isPublished === true,
  };
}

export function PublicPeoplePage({ collectionName, title, description }: PublicPeoplePageProps) {
  const [items, setItems] = useState<PublicPerson[]>([]);
  const [form, setForm] = useState<PublicPerson>(EMPTY);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [crop, setCrop] = useState<CropState | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const [mediaPolicy, setMediaPolicy] = useState<PublicWebMediaPolicy>(DEFAULT_PUBLIC_WEB_MEDIA_POLICY);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => `${item.name} ${item.role} ${item.bio} ${item.id}`.toLowerCase().includes(needle));
  }, [filter, items]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(collection(db, collectionName), orderBy("sortOrder", "asc"), limit(200)));
      setItems(snap.docs.map((item) => mapDoc(item.id, item.data())));
    } catch (e: any) {
      setError(e?.message || `Failed to load ${title}.`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    setForm(EMPTY);
    void loadPublicWebMediaPolicy().then(setMediaPolicy);
  }, [collectionName]);

  function beginEdit(item: PublicPerson) {
    setForm(item);
    setError(null);
    setSuccess(null);
    setCrop(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetForm() {
    setForm(EMPTY);
    setError(null);
    setSuccess(null);
    setCrop(null);
  }

  function getDraftId() {
    return form.id.trim() || doc(collection(db, collectionName)).id;
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const name = form.name.trim();
      const role = form.role.trim();
      const id = getDraftId();
      if (!role) throw new Error("Role is required.");

      await setDoc(
        doc(db, collectionName, id),
        {
          id,
          name,
          role,
          bio: form.bio.trim(),
          email: form.email.trim(),
          imageUrl: form.imageUrl.trim(),
          imageStoragePath: form.imageStoragePath ?? "",
          sortOrder: asNumber(form.sortOrder, 999),
          isPublished: form.isPublished === true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setForm((prev) => ({ ...prev, id }));
      setSuccess("Saved.");
      await load();
    } catch (e: any) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleFileSelected(fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    setError(null);
    setSuccess(null);

    if (!file.type.startsWith("image/")) {
      setError("Choose a JPG, PNG, or WEBP image.");
      return;
    }
    if (file.size > mediaPolicy.maxStaffPhotoBytes) {
      setError(`Choose an image under ${formatBytesMb(mediaPolicy.maxStaffPhotoBytes)}.`);
      return;
    }

    try {
      const src = await readFileAsDataUrl(file);
      const image = await loadImage(src);
      setCrop({
        src,
        fileName: file.name,
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
        offsetX: 0,
        offsetY: 0,
        zoom: 1,
      });
    } catch (e: any) {
      setError(e?.message || "Could not read image.");
    }
  }

  async function editCurrentImage() {
    if (!form.imageUrl) return;
    setError(null);
    setSuccess(null);
    try {
      const src = await imageUrlToDataUrl(form.imageUrl);
      const image = await loadImage(src);
      setCrop({
        src,
        fileName: `${form.id || collectionName}-photo.png`,
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
        offsetX: 0,
        offsetY: 0,
        zoom: 1,
      });
    } catch (e: any) {
      setError(e?.message || "Could not open current image for editing. Upload the original image again.");
    }
  }

  async function createCroppedBlob() {
    if (!crop) throw new Error("No image selected.");
    const image = await loadImage(crop.src);
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Image editor is not available in this browser.");

    ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.save();
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    const baseScale = Math.max(OUTPUT_SIZE / image.naturalWidth, OUTPUT_SIZE / image.naturalHeight);
    const scale = baseScale * crop.zoom;
    const offsetScale = OUTPUT_SIZE / EDITOR_SIZE;
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    const dx = OUTPUT_SIZE / 2 - drawWidth / 2 + crop.offsetX * offsetScale;
    const dy = OUTPUT_SIZE / 2 - drawHeight / 2 + crop.offsetY * offsetScale;

    ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
    ctx.restore();
    return canvasToBlob(canvas);
  }

  async function uploadCroppedImage() {
    setUploadingImage(true);
    setError(null);
    setSuccess(null);
    try {
      const role = form.role.trim();
      const id = getDraftId();
      if (!role) throw new Error("Enter a role before uploading a photo.");

      const blob = await createCroppedBlob();
      const storagePath = `${collectionName}/${id}/profile-${Date.now()}.png`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, { contentType: "image/png" });
      const imageUrl = await getDownloadURL(storageRef);
      if (form.imageStoragePath && form.imageStoragePath !== storagePath) {
        await deleteObject(ref(storage, form.imageStoragePath)).catch(() => undefined);
      }
      setForm((prev) => ({ ...prev, id, imageUrl, imageStoragePath: storagePath }));
      setCrop(null);
      setSuccess("Cropped photo uploaded. Save the entry to publish it.");
    } catch (e: any) {
      setError(e?.message || "Failed to upload cropped image.");
    } finally {
      setUploadingImage(false);
    }
  }

  async function removeImage() {
    if (!form.imageUrl) return;
    setUploadingImage(true);
    setError(null);
    setSuccess(null);
    try {
      if (form.imageStoragePath) await deleteObject(ref(storage, form.imageStoragePath)).catch(() => undefined);
      setForm((prev) => ({ ...prev, imageUrl: "", imageStoragePath: "" }));
      setSuccess("Photo removed. Save the entry to publish the change.");
    } catch (e: any) {
      setError(e?.message || "Could not remove photo.");
    } finally {
      setUploadingImage(false);
    }
  }

  async function togglePublished(item: PublicPerson) {
    setBusyId(item.id);
    try {
      await setDoc(doc(db, collectionName, item.id), { isPublished: !item.isPublished, updatedAt: serverTimestamp() }, { merge: true });
      await load();
    } catch (e: any) {
      setError(e?.message || "Update failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(item: PublicPerson) {
    if (!confirm(`Delete ${item.role}${item.name ? ` - ${item.name}` : ""}?`)) return;
    setBusyId(item.id);
    try {
      if (item.imageStoragePath) await deleteObject(ref(storage, item.imageStoragePath)).catch(() => undefined);
      await deleteDoc(doc(db, collectionName, item.id));
      if (form.id === item.id) setForm(EMPTY);
      await load();
    } catch (e: any) {
      setError(e?.message || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  }

  function onCropPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (!crop) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragStart({ x: event.clientX, y: event.clientY, offsetX: crop.offsetX, offsetY: crop.offsetY });
  }

  function onCropPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!crop || !dragStart) return;
    setCrop({
      ...crop,
      offsetX: clampOffset(dragStart.offsetX + event.clientX - dragStart.x),
      offsetY: clampOffset(dragStart.offsetY + event.clientY - dragStart.y),
    });
  }

  function onCropPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    setDragStart(null);
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <h2>{title}</h2>
        <p className="muted">{description}</p>
        {error ? <div className="error">{error}</div> : null}
        {success ? <div className="success">{success}</div> : null}
      </div>

      <div className="card">
        <h3>{form.id ? "Edit Entry" : "Create Entry"}</h3>
        <div className="grid2">
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Record ID
            <input className="input" value={form.id || "Auto-assigned when saved"} readOnly disabled />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Sort Order
            <input className="input" type="number" value={form.sortOrder} onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: asNumber(e.target.value, prev.sortOrder) }))} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Name
            <input className="input" value={form.name} placeholder="Leave blank if open/TBD" onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Role
            <input className="input" value={form.role} placeholder="President" onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Public Email
            <input className="input" type="email" value={form.email} placeholder="name@zionclassical.org" onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
          </label>
        </div>
        <p className="muted" style={{ marginTop: 10 }}>
          Record IDs are system-managed and do not reference a person's name, so names can be updated safely.
        </p>
        <label className="muted" style={{ display: "grid", gap: 6, marginTop: 12 }}>
          Bio / Notes
          <textarea className="input" rows={3} value={form.bio} onChange={(e) => setForm((prev) => ({ ...prev, bio: e.target.value }))} />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 16, alignItems: "center", marginTop: 12 }}>
          <div>
            {form.imageUrl ? (
              <img src={form.imageUrl} alt="" style={{ width: 104, height: 104, borderRadius: 999, objectFit: "cover", border: "1px solid var(--border)" }} />
            ) : (
              <InitialAvatar name={form.name} role={form.role} size={104} />
            )}
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <b>Optional Photo</b>
              <div className="muted">Crop to a centered circular headshot. The website displays Board and PTU photos the same way it displays staff photos.</div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={(event) => {
                void handleFileSelected(event.target.files);
                event.currentTarget.value = "";
              }}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="btn secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                Upload & Crop
              </button>
              {form.imageUrl ? <button className="btn secondary" type="button" onClick={() => void editCurrentImage()} disabled={uploadingImage}>Re-edit Current Photo</button> : null}
              {form.imageUrl ? <button className="btn secondary" type="button" onClick={() => void removeImage()} disabled={uploadingImage}>Remove Photo</button> : null}
            </div>
          </div>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
          <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))} />
          <span>Published on public website</span>
        </label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
          <button className="btn" onClick={() => void save()} disabled={saving || uploadingImage}>{saving ? "Saving..." : "Save"}</button>
          <button className="btn secondary" onClick={resetForm} disabled={saving || uploadingImage}>Clear</button>
        </div>
      </div>

      {crop ? (
        <div className="card" style={{ borderColor: "rgba(22,58,95,0.28)" }}>
          <h3>Crop Photo</h3>
          <div className="muted">Drag the photo until the face is centered in the circular vignette, then adjust zoom if needed. The saved file is a 512 x 512 circular PNG.</div>
          <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 18, alignItems: "start", marginTop: 14 }}>
            <div
              onPointerDown={onCropPointerDown}
              onPointerMove={onCropPointerMove}
              onPointerUp={onCropPointerUp}
              onPointerCancel={onCropPointerUp}
              style={{
                width: EDITOR_SIZE,
                height: EDITOR_SIZE,
                overflow: "hidden",
                position: "relative",
                background: "#111827",
                border: "1px solid var(--border)",
                borderRadius: 16,
                touchAction: "none",
                cursor: dragStart ? "grabbing" : "grab",
              }}
            >
              <img
                src={crop.src}
                alt=""
                draggable={false}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: `${getEditorImageSize(crop).width}px`,
                  height: `${getEditorImageSize(crop).height}px`,
                  transform: `translate(calc(-50% + ${crop.offsetX}px), calc(-50% + ${crop.offsetY}px))`,
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 22,
                  borderRadius: 999,
                  border: "3px solid var(--gold)",
                  boxShadow: "0 0 0 999px rgba(15, 23, 42, 0.52)",
                  pointerEvents: "none",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: 1,
                  height: "76%",
                  background: "rgba(255,255,255,0.38)",
                  transform: "translateX(-50%)",
                  pointerEvents: "none",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "12%",
                  top: "50%",
                  width: "76%",
                  height: 1,
                  background: "rgba(255,255,255,0.38)",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
              />
            </div>
            <div style={{ display: "grid", gap: 14 }}>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                Zoom
                <input
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.05}
                  value={crop.zoom}
                  onChange={(e) => setCrop((prev) => prev ? { ...prev, zoom: Number(e.target.value) } : prev)}
                />
              </label>
              <div style={{ display: "grid", gap: 8 }}>
                <div className="muted">Drag the photo or fine tune</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 64px)", gap: 6, width: 204 }}>
                  <span />
                  <button type="button" className="btn small secondary" onClick={() => setCrop((prev) => prev ? { ...prev, offsetY: clampOffset(prev.offsetY - 8) } : prev)}>Up</button>
                  <span />
                  <button type="button" className="btn small secondary" onClick={() => setCrop((prev) => prev ? { ...prev, offsetX: clampOffset(prev.offsetX - 8) } : prev)}>Left</button>
                  <button type="button" className="btn small secondary" onClick={() => setCrop((prev) => prev ? { ...prev, offsetX: 0, offsetY: 0 } : prev)}>Center</button>
                  <button type="button" className="btn small secondary" onClick={() => setCrop((prev) => prev ? { ...prev, offsetX: clampOffset(prev.offsetX + 8) } : prev)}>Right</button>
                  <span />
                  <button type="button" className="btn small secondary" onClick={() => setCrop((prev) => prev ? { ...prev, offsetY: clampOffset(prev.offsetY + 8) } : prev)}>Down</button>
                  <span />
                </div>
              </div>
              <div className="muted">Tip: for rectangle photos, keep the face inside the gold circle and use the dimmed outer area as extra room while positioning.</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="btn" onClick={() => void uploadCroppedImage()} disabled={uploadingImage || saving}>
                  {uploadingImage ? "Uploading..." : "Save Cropped Photo"}
                </button>
                <button className="btn secondary" onClick={() => setCrop({ ...crop, offsetX: 0, offsetY: 0, zoom: MIN_ZOOM })} disabled={uploadingImage}>
                  Reset Crop
                </button>
                <button className="btn secondary" onClick={() => setCrop(null)} disabled={uploadingImage}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <h3 style={{ marginBottom: 4 }}>Entries</h3>
          <input className="input" style={{ maxWidth: 360 }} value={filter} placeholder="Search..." onChange={(e) => setFilter(e.target.value)} />
        </div>
        <div className="table" style={{ marginTop: 14 }}>
          <div className="thead" style={{ gridTemplateColumns: "80px 90px 1fr 1fr 120px 240px" }}>
            <div>Order</div><div>Photo</div><div>Name</div><div>Role</div><div>Status</div><div></div>
          </div>
          {loading ? (
            <div className="trow" style={{ gridTemplateColumns: "1fr" }}><div className="muted">Loading...</div></div>
          ) : filtered.length === 0 ? (
            <div className="trow" style={{ gridTemplateColumns: "1fr" }}><div className="muted">No entries found.</div></div>
          ) : filtered.map((item) => (
            <div className="trow" key={item.id} style={{ gridTemplateColumns: "80px 90px 1fr 1fr 120px 240px" }}>
              <div>{item.sortOrder}</div>
              <div>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt="" style={{ width: 48, height: 48, borderRadius: 999, objectFit: "cover", border: "1px solid var(--border)" }} />
                ) : (
                  <InitialAvatar name={item.name} role={item.role} size={48} />
                )}
              </div>
              <div>
                <b>{item.name || "Open"}</b>
                <div className="muted" style={{ fontSize: 12 }}>{item.id}</div>
                {item.email ? <div className="muted" style={{ fontSize: 12 }}>{item.email}</div> : null}
              </div>
              <div>{item.role}</div>
              <div><span className={item.isPublished ? "pill ok" : "pill off"}>{item.isPublished ? "Published" : "Hidden"}</span></div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button className="btn small secondary" onClick={() => beginEdit(item)} disabled={busyId === item.id}>Edit</button>
                <button className="btn small secondary" onClick={() => void togglePublished(item)} disabled={busyId === item.id}>{item.isPublished ? "Hide" : "Publish"}</button>
                <button className="btn small secondary" onClick={() => void remove(item)} disabled={busyId === item.id}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
